/**
 * aiDraft — pure helpers for mapping an AI parse-task response to
 * QuickAddTask form fields.
 *
 * Kept separate so the mapping logic is unit-testable without mounting
 * a component. Mirrors the buildAiSettingsPayload pattern in api.ts.
 */

/** Shape returned by the /api/ai/parse-task endpoint. */
export interface AiDraft {
  title: string;
  priority?: string;
  /** ISO date string (YYYY-MM-DD) — stored as `due_date` by the server. */
  due_date?: string;
}

/** Shape of form fields inside QuickAddTask that the draft populates. */
export interface AiDraftFields {
  title: string;
  /** Maps to the `priority` controlled input. */
  priority: string;
  /** Maps to the `end_date` field (due_date → end_date). */
  end_date: string;
}

/**
 * Map an AiDraft (from the parse-task API) to QuickAddTask form fields.
 *
 * - `due_date` from the draft maps to `end_date` (the field name used by createTask).
 * - Missing optional fields default to empty string so controlled inputs
 *   stay controlled (never undefined).
 * - `title` is trimmed; if blank after trim the original (untrimmed) value is kept.
 */
export function applyAiDraft(draft: AiDraft): AiDraftFields {
  const title = draft.title.trim() || draft.title;
  return {
    title,
    priority: draft.priority ?? "",
    end_date: draft.due_date ?? "",
  };
}

/**
 * Maps the canonical priority values to the German token strings that
 * parseQuickAdd recognises. English variants (!high etc.) also work, but we
 * emit the German forms to stay consistent with the UI label map.
 */
const PRIORITY_TOKEN: Record<string, string> = {
  urgent: "!dringend",
  high: "!hoch",
  medium: "!mittel",
  low: "!niedrig",
};

/**
 * Build a quick-add prefill string from AiDraftFields so that the result can
 * be fed back into parseQuickAdd and yields the original priority / due-date.
 *
 * Format: `<title> [<priorityToken>] [<DD.MM.YYYY>]`
 *
 * - priority token is only appended when the priority value is a known key.
 * - end_date (YYYY-MM-DD) is converted to DD.MM.YYYY and appended when valid.
 * - On any parsing failure the field is silently omitted (never throws).
 */
export function buildQuickAddString(fields: AiDraftFields): string {
  const parts: string[] = [fields.title];

  if (fields.priority) {
    const token = PRIORITY_TOKEN[fields.priority];
    if (token !== undefined) {
      parts.push(token);
    }
  }

  if (fields.end_date) {
    // Expect YYYY-MM-DD
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fields.end_date);
    if (match !== null) {
      const [, yyyy, mm, dd] = match;
      parts.push(`${dd}.${mm}.${yyyy}`);
    }
  }

  return parts.join(" ");
}
